import { Injectable } from '@angular/core';

import { DxParamsModel } from '../models/dx-params.model';
import { PrimeNgParamsModel } from '../models/primeng-params.model';
import { DxMatchModeType } from '../types/devex-match-mode.type';
import { PrimengMatchModeType } from '../types/primeng-match-mode.type';
import { PrimeNgFilter } from './../models/primeng-filter.model';

@Injectable()
export class ValUtilsService {
    /**
     * Validates if input value is object
     * @param obj input value
     */
    public isObject(obj: any) {
        return obj === Object(obj);
    }

    /**
     * Validates if input value is string
     * @param s input value
     */
    public isString(s: any) {
        return typeof (s) === 'string' || s instanceof String;
    }

    /**
     * Validates if input value is primitive type
     * @param arg input value
     */
    public isPrimitive(arg: any) {
        const type = typeof arg;

        return arg == null || (type !== 'object' && type !== 'function');
    }

    /**
     * Validates if input value is array
     * @param arr input value
     */
    public isArray(arr: any) {
        return Array.isArray ? Array.isArray(arr) : arr instanceof Array;
    }

    /**
     * Validates if input value is numeric (number or string)
     * @param input value
     */
    public isNumeric(n: any) {
        return !isNaN(parseFloat(n)) && isFinite(n);
    }

    /**
     * Validates if input value is function
     * @param f input value
     */
    public isFunction(f: any): boolean {
        return typeof (f) === 'function';
    }

    /**
     * Checks if string is null or empty
     * @param value string to check
     */
    public isStringNullOrEmpty(value: string): boolean {
        if (!value || value === '') {
            return true;
        }

        return false;
    }

    /**
     * Checks if string is null or empty or whitespace
     * @param value string to check
     */
    public isStringNullOrWhitespace(value: string): boolean {
        if (!value || value === '' || value.trim() === '') {
            return true;
        }

        return false;
    }

    /**
     * Serializes date to ISO format without zone
     * @param date input value
     */
    public serializeDate(date: Date): string {
        const builder = [1 + date.getMonth(), '/', date.getDate(), '/', date.getFullYear()];
        const h = date.getHours();
        const m = date.getMinutes();
        const s = date.getSeconds();
        const f = date.getMilliseconds();

        if (h + m + s + f > 0) {
            builder.push(' ', this.zeroPad(h, 2), ':', this.zeroPad(m, 2), ':', this.zeroPad(s, 2), '.', this.zeroPad(f, 3));
        }

        return builder.join('');
    }

    /**
     * Ads leading zeros if needed
     * @param value input value
     * @param len output string length
     */
    public zeroPad(value: number, len: number): string {
        let text = String(value);

        while (text.length < len) {
            text = '0' + text;
        }

        return text;
    }

    /**
     * Copy the values of all enumerable properties from one or more source
     * objects to a target object. It will return the target object.
     * @param target Target object
     * @param source Source objects
     */
    public shallowCopy<TObject extends object>(target: TObject, ...sources: any[]): TObject {
        return Object.assign(target, ...sources);
    }

    /** Search max value of numeric array, or array of objects
     * @param arr Source array
     * @param fieldName field in object on which comparison is made or null if simple array
     */
    public arrayMax<TModel>(arr: Array<TModel>, fieldName: string = null): TModel {
        if (!arr || arr.length === 0) { return null; }
        if (fieldName === null) {
            return arr.reduce((prev, curr) => {
                return (prev > curr ? prev : curr);
            });
        } else {
            return arr.reduce((prev, curr) => {
                return (prev[fieldName] > curr[fieldName] ? prev : curr);
            });
        }
    }

    /**
     * Search min value of numeric array, or array of objects
     * @param arr Source array
     * @param fieldName field in object on which comparison is made or null if simple array
     */
    public arrayMin<TModel>(arr: Array<TModel>, fieldName: string = null): TModel {
        if (!arr || arr.length === 0) { return null; }
        if (fieldName === null) {
            return arr.reduce((prev, curr) => {
                return (prev < curr ? prev : curr);
            });
        } else {
            return arr.reduce((prev, curr) => {
                return (prev[fieldName] < curr[fieldName] ? prev : curr);
            });
        }
    }

    /**
     * Sorts numeric array, or array of objects
     * @param arr Source array
     * @param fieldName field in object on which comparison is made or null if simple array
     * @param desc descending
     */
    public arraySort<TModel>(arr: Array<TModel>, fieldName: string = null, desc = false): Array<TModel> {
        if (fieldName === null) {
            return arr.sort((a, b) => {
                return a < b ? (desc ? 1 : -1) : a === b ? 0 : (desc ? -1 : 1);
            });
        } else {
            return arr.sort((a, b) => {
                return a[fieldName] < b[fieldName] ? (desc ? 1 : -1) : a[fieldName] === b[fieldName] ? 0 : (desc ? -1 : 1);
            });
        }
    }

    /**
     * Copies input object, if input is primitive returns src
     * @param src Source object
     */
    public copyObject<TObject extends object>(src: TObject): TObject {
        if (this.isPrimitive(src)) { return src; }
        const dest = {} as TObject;
        Object.keys(src).forEach(o => {
            dest[o] = src[o];
        });
        return dest;
    }

    /**
     * Deep copies input object (all child objects are copied), if input is primitive returns src
     * @param src Source object
     */
    public deepCopyObject<TObject extends object>(src: TObject): TObject {
        if (this.isPrimitive(src)) { return src; }
        const dest = {} as TObject;
        Object.keys(src).forEach(o => {
            if (this.isArray(src[o])) {
                dest[o] = this.copyArray(src[o]);
            } else if (src[o] instanceof Date) {
                dest[o] = this.copyDate(src[o]);
            } else {
                dest[o] = this.deepCopyObject(src[o]);
            }
        });
        return dest;
    }

    /**
     * Copies input array, if input is not array returns src
     * @param src Source object
     */
    public copyArray<TObject extends object>(src: Array<TObject>): Array<TObject> {
        if (!this.isArray(src)) { return src; }
        const dest = new Array<TObject>();
        src.forEach(x => {
            dest.push(this.copyObject(x));
        });
        return dest;
    }

    /**
     * Merges all common field values of two objects and returns desination object
     * @param src Source object
     * @param dst Destination object
     */
    public mergeObjectValues<SObject extends object, DObject extends object>(src: SObject, dst: DObject): DObject {
        if (this.isPrimitive(src)) { return dst; }
        Object.keys(src).forEach(o => {
            if (dst[o] !== undefined) {
                dst[o] = src[o];
            }
        });
        return dst;
    }

    /**
     * Deep merges all common field values of two objects and returns desination object
     * @param src Source object
     * @param dst Destination object
     */
    public deepMergeObjectValues<SObject extends object, DObject extends object>(src: SObject, dst: DObject): DObject {
        if (this.isPrimitive(src)) { return dst; }

        Object.keys(src).forEach(o => {
            if (dst !== null && dst[o] !== undefined) {
                if (this.isPrimitive(src[o])) {
                    dst[o] = src[o];
                } else {
                    dst[o] = this.deepMergeObjectValues(src[o], dst[o]);
                }
            }
        });

        return dst;
    }

    /**
     * Merges fields of two objects, returns dst
     * @param src Source object
     * @param dst Destination object
     */
    public mergeObjects<SObject extends object, DObject extends object>(src: SObject, dst: DObject): DObject {
        if (this.isPrimitive(src)) { return dst; }
        Object.keys(src).forEach(o => {
            dst[o] = src[o];
        });
        return dst;
    }

    /**
     * Detects difference beetwen values in object
     * @param obj1 first object
     * @param obj2 second object
     */
    public areObjectsEqual<SObject extends object, DObject extends object>(obj1: SObject, obj2: DObject, maxDecimals = 4): boolean {
        if (this.isPrimitive(obj1)) { return (obj2 as any) === (obj1 as any); }
        const keys = Object.keys(obj1);
        for (let i = 0; i < keys.length; i++) {
            const o = keys[i];
            // do not compare guids
            if (o !== 'guid') {
                if (obj2 === null || obj2[o] === undefined) { return false; }
                if (this.isPrimitive(obj1[o])) {
                    // compare numerics to 4 decimale
                    if (this.isNumeric(obj1[o])) {
                        const s = this.roundNumber(obj1[o], maxDecimals);
                        if (this.isNumeric(obj2[o])) {
                            const d = this.roundNumber(obj2[o], maxDecimals);
                            if (s !== d) {
                                return false;
                            }
                        } else {
                            return false;
                        }
                    } else {
                        if (obj2[o] !== obj1[o]) {
                            return false;
                        }
                    }
                } else {
                    if (this.areObjectsEqual(obj1[o], obj2[o]) === false) {
                        return false;
                    }
                }
            }
        }

        return true;
    }

    /**
     * Returns numerical representation of string
     * @param input Source string
     */
    public getStringHashCode(input: string): number {
        let hash = 0;
        let chr = 0;
        if (input.length === 0) { return hash; }
        for (let i = 0; i < input.length; i++) {
            chr = input.charCodeAt(i);
            // eslint-disable-next-line no-bitwise
            hash = ((hash << 5) - hash) + chr;
            // eslint-disable-next-line no-bitwise
            hash |= 0; // Convert to 32bit integer
        }
        return hash;
    }

    /**
     * Returns numeric representation of object
     * @param obj object
     */
    public getObjectHashCode<SObject extends object>(
        obj: SObject, excludeFields: Array<string> = ['guid'], maxDecimals = 4
    ): number {
        let ret = 0;
        if (this.isPrimitive(obj)) {
            return this.getStringHashCode(obj + '');
        }
        if (obj instanceof Date) {
            return this.getStringHashCode((obj as Date).toString());
        }
        if (this.isArray(obj)) {
            (obj as Array<any>).forEach(x => {
                ret += this.getObjectHashCode(x, excludeFields);
            });
            return ret;
        }
        const keys = Object.keys(obj);
        for (let i = 0; i < keys.length; i++) {
            const o = keys[i];
            // do not calculate guids
            if (!excludeFields.includes(o)) {
                if (this.isPrimitive(obj[o])) {
                    if (this.isNumeric(obj[o])) {
                        const s = this.roundNumber(obj[o], maxDecimals);
                        ret += this.getStringHashCode(o + '') + this.getStringHashCode(s + '');
                    } else {
                        ret += this.getStringHashCode(o + '') + this.getStringHashCode(obj[o] + '');
                    }
                } else if (obj instanceof Date) {
                    ret += this.getStringHashCode(o + '') + this.getStringHashCode((obj as Date).toString());
                } else {
                    ret += this.getObjectHashCode(obj[o], excludeFields);
                }
            }
        }

        return ret;
    }

    /**
     * Deeps scan object and sets specified property to specified value
     * @param obj Object to scan
     * @param propertName property name
     * @param value new value
     */
    public deepSetObjectProperty<T extends object>(obj: T, propertyName: string, value: any): void {
        if (!obj) {
            return;
        }

        Object.keys(obj).forEach(key => {
            const o = obj[key];

            if (o) {
                if (this.isArray(o)) {
                    (o as Array<any>).forEach(el => {
                        this.deepSetObjectProperty(el, propertyName, value);
                    });
                } else {
                    if (Object.prototype.hasOwnProperty.call(o, propertyName)) {
                        o[propertyName] = value;
                    } else if (!this.isPrimitive(o) && !this.isFunction(o)) {
                        this.deepSetObjectProperty(o, propertyName, value);
                    } else if (key === propertyName) {
                        obj[key] = value;
                    }
                }
            }
        });
    }

    /**
     * Copies date object, returns new date
     * @param input Source date or string in date format
     */
    public copyDate = (input: string | Date): Date => {
        let tmp: Date = input as Date;
        if (!input) {
            return null;
        }
        if (!(input instanceof Date)) {
            tmp = new Date(input);
        }
        const hours = tmp.getHours();
        const minutes = tmp.getMinutes();
        const miliseconds = tmp.getMilliseconds();
        const seconds = tmp.getSeconds();
        const year = tmp.getFullYear();
        const month = tmp.getMonth();
        const day = tmp.getDate();
        return new Date(year, month, day, hours, minutes, seconds, miliseconds);
    }

    /**
     * Always returns date, except input is null or undefined, then returns null
     * @param input Source date or string in date format
     */
    public getDate = (input: string | Date): Date => {
        if (!input) {
            return null;
        }
        if (!(input instanceof Date)) {
            const timestamp = Date.parse(input);

            if (isNaN(timestamp) === false) {
                return new Date(input);
            }
            return null;
        } else {
            // invalid date
            if (isNaN(input.getTime())) {
                return null;
            }
        }
        return input as Date;
    }

    /**
     * Always returns string, except input is null or undefined, then returns null
     * @param input Source date or string in date format
     */
    public getDateOnlyUtcIsoString = (input: string | Date): string => {
        let d: Date = null;
        if (!input) {
            return null;
        }
        if (!(input instanceof Date)) {
            const timestamp = Date.parse(input);

            if (isNaN(timestamp) === false) {
                d = new Date(input);
            }
        } else {
            // invalid date
            if (isNaN(input.getTime())) {
                return null;
            }
            d = input;
        }
        return d.getFullYear() + '-' + this.zeroPad((1 + d.getMonth()), 2) + '-' + this.zeroPad(d.getDate(), 2) + 'T00:00:00.000Z';
    }

    /**
     * Always returns string, except input is null or undefined, then returns null
     * @param input Source date or string in date format
     */
     public getDateOnlyString = (input: string | Date): string => {
        let d: Date = null;
        if (!input) {
            return null;
        }
        if (!(input instanceof Date)) {
            const timestamp = Date.parse(input);

            if (isNaN(timestamp) === false) {
                d = new Date(input);
            }
        } else {
            // invalid date
            if (isNaN(input.getTime())) {
                return null;
            }
            d = input;
        }
        return d.getFullYear() + '-' + this.zeroPad((1 + d.getMonth()), 2) + '-' + this.zeroPad(d.getDate(), 2);
    }

    /**
     * Always returns string, except input is null or undefined, then returns null
     * @param input Source date or string in date format
     */
    public getDateAsIsoString = (input: Date): string => {
        return input.getFullYear() + '-' + this.zeroPad((1 + input.getMonth()), 2) + '-' + this.zeroPad(input.getDate(), 2) +
            'T' + this.zeroPad(input.getHours(), 2) + ':' + this.zeroPad(input.getMinutes(), 2) + ':00.000Z';
    }

    /**
     * Always returns date, except input is null or undefined, then returns null
     * @param input Source date or string in date format
     */
    public getDateWithTimeSetToZero = (input: string | Date): Date => {
        if (!input) {
            return null;
        }
        if (!(input instanceof Date)) {
            input = new Date(input);
        }
        return new Date(input.getFullYear(), input.getMonth(), input.getDate(), 0, 0, 0, 0);
    }

    /**
     * Rounds a number to specific decimal places, if input is null or undefined returns 0
     * @param input - number
     * @param decimalPlaces - decimal places
     * @param return0 - if TRUE and input is null or undefined returns 0 else returns input
     */
    public roundNumber = (input: number, decimalPlaces: number = 0, return0: boolean = false): number => {
        if (!input) { return return0 ? 0 : input; }
        if (!decimalPlaces) { decimalPlaces = 0; }
        const tmp = Math.pow(10, decimalPlaces);
        return Math.round(input * tmp) / tmp;
    }

    /**
     * Create unique ID - integer only - from current timestamp with some randomization
     * @param length - wanted length of ID, default is 15
     */
    public uniqueIdGenerator = (length: number = 15): number => {
        const _getRandomInt = (min, max) => {
            return Math.floor(Math.random() * (max - min + 1)) + min;
        };

        const timestamp = +new Date();
        const ts = timestamp.toString();
        const parts = ts.split('').reverse();
        let id = '';

        for (let i = 0; i < length; ++i) {
            const index = _getRandomInt(0, parts.length - 1);
            id += parts[index];
        }

        return parseInt(id, 10);
    }

    /**
     * Create unique fake GUID (in correct format) from random numbers
     */
    public guid = () => {
        // eslint-disable-next-line no-bitwise
        return ((((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1) +
            // eslint-disable-next-line no-bitwise
            (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1) + '-' +
            // eslint-disable-next-line no-bitwise
            (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1) + '-4' +
            // eslint-disable-next-line no-bitwise
            (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1).substr(0, 3) + '-' +
            // eslint-disable-next-line no-bitwise
            (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1) + '-' +
            // eslint-disable-next-line no-bitwise
            (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1) +
            // eslint-disable-next-line no-bitwise
            (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1) +
            // eslint-disable-next-line no-bitwise
            (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1)).toLowerCase();
    }

    /**
     * Url Base 64 decode
     * @param str string to decode
     */
    public urlBase64Decode(str: string): string {
        let res = str.replace(/-/g, '+').replace(/_/g, '/');

        switch (res.length % 4) {
            case 0: {
                break;
            }
            case 2: {
                res += '==';
                break;
            }
            case 3: {
                res += '=';
                break;
            }
            default: {
                throw new Error('Illegal base64url string');
            }
        }

        return this.b64DecodeUnicode(res);
    }

    /**
     * Base 64 string decoder
     * @param str string to decode
     */
    public b64decode(str: string): string {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
        let res = '';

        str = String(str).replace(/=+$/, '');

        if (str.length % 4 === 1) {
            throw new Error('Not base 64 string.');
        }

        for (let bc = 0, bs = void 0, buffer = void 0, idx = 0;
            // eslint-disable-next-line no-cond-assign
            (buffer = str.charAt(idx++));
            // eslint-disable-next-line no-bitwise
            ~buffer &&
                // eslint-disable-next-line no-cond-assign
                ((bs = bc % 4 ? bs * 64 + buffer : buffer),
                    bc++ % 4)
                // eslint-disable-next-line no-bitwise
                ? (res += String.fromCharCode(255 & (bs >> ((-2 * bc) & 6))))
                : 0) {
            buffer = chars.indexOf(buffer);
        }

        return res;
    }

    /**
     * Unicode Base 64 decoder
     * @param str string to decode
     */
    public b64DecodeUnicode(str: string): string {
        return decodeURIComponent(Array.prototype.map
            .call(this.b64decode(str), (c) => {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join(''));
    }

    /**
     * Validates Oib value
     * @param oib Oib to check
     */
    public isOibValid(oib: string): boolean {
        if (!oib) { return false; }

        oib = oib.toString();

        if (oib.length !== 11) { return false; }

        if (isNaN(parseInt(oib, 10))) { return false; }

        let tmp = 10;
        for (let i = 0; i < 10; i++) {
            tmp += parseInt(oib.substr(i, 1), 10);
            tmp = tmp % 10;
            tmp = tmp === 0 ? 10 : tmp;
            tmp *= 2;
            tmp = tmp % 11;
        }
        let cont = 11 - tmp;
        cont = cont === 10 ? 0 : cont;

        return cont === parseInt(oib.substr(10, 1), 10);
    }

    public toMnemonic(value: string): string {
        if (!value) {
            return value;
        }

        value = value.replace(/[^a-zA-Z0-9_]/gi, '');

        return value ? value.toUpperCase() : value;
    }
    public mapPrimeNgtoDxParameters = (
        input: PrimeNgParamsModel,
        additionalFilters: Array<{ name: string, value: Array<PrimeNgFilter> }> = null
    ): DxParamsModel => {
        let out = new DxParamsModel('', '', null, input.first, input.rows);
        const outFilter = [];
        if (input.filters) {
            Object.keys(input.filters).forEach(o => {
                const fieldName: string = o;
                const details: Array<PrimeNgFilter> = input.filters[o] as Array<PrimeNgFilter>;
                let tmpFilter: any;
                if (details && details.filter) {
                    if (details.filter(x => x.value).length > 1) {
                        tmpFilter = [];
                        details.filter(x => x.value).forEach(x => {
                            const matchMode = this.MapMatchMode(x.matchMode);
                            const value = x.value;
                            const operator = x.operator;
                            (tmpFilter as Array<any>).push([fieldName, matchMode, value]);
                            (tmpFilter as Array<any>).push(operator);
                        });
                        if ((tmpFilter as Array<any>).length > 1) {
                            (tmpFilter as Array<any>).splice((tmpFilter as Array<any>).length - 1, 1);
                            outFilter.push(tmpFilter);
                            outFilter.push('and');
                        }
                    } else {
                        tmpFilter = null;
                        details.filter(x => x.value).forEach(x => {
                            const matchMode = this.MapMatchMode(x.matchMode);
                            const value = x.value;
                            tmpFilter = [fieldName, matchMode, value];
                        });
                        if (tmpFilter) {
                            outFilter.push(tmpFilter);
                            outFilter.push('and');
                        }
                    }
                }
            });
        }
        if (additionalFilters) {
            additionalFilters.forEach(f => {
                const fieldName: string = f.name;
                let tmpFilter: any;
                if (f.value.length > 1) {
                    tmpFilter = [];
                    f.value.forEach(x => {
                        const matchMode = this.MapMatchMode(x.matchMode);
                        const value = x.value;
                        const operator = x.operator;
                        (tmpFilter as Array<any>).push([fieldName, matchMode, value]);
                        (tmpFilter as Array<any>).push(operator);
                    });
                    if ((tmpFilter as Array<any>).length > 1) {
                        (tmpFilter as Array<any>).splice((tmpFilter as Array<any>).length - 1, 1);
                        outFilter.push(tmpFilter);
                        outFilter.push('and');
                    }
                } else {
                    tmpFilter = null;
                    f.value.forEach(x => {
                        const matchMode = this.MapMatchMode(x.matchMode);
                        const value = x.value;
                        tmpFilter = [fieldName, matchMode, value];
                    });
                    if (tmpFilter) {
                        outFilter.push(tmpFilter);
                        outFilter.push('and');
                    }
                }
            });
        }
        if (outFilter.length > 1) {
            outFilter.splice(outFilter.length - 1, 1);
        }
        out.filter = JSON.stringify(outFilter);
        const outSort = [{ selector: input.sortField, desc: input.sortOrder < 0 }];
        out.sort = JSON.stringify(outSort);
        return out;
    }
    public MapMatchMode = (input: PrimengMatchModeType): DxMatchModeType => {
        let outOperator: DxMatchModeType;
        switch (input) {
            case 'startsWith':
                outOperator = 'startswith';
                break;
            case 'contains':
                outOperator = 'contains';
                break;
            case 'notContains':
                outOperator = 'notcontains';
                break;
            case 'endsWith':
                outOperator = 'endswith';
                break;
            case 'equals':
                outOperator = '=';
                break;
            case 'notEquals':
                outOperator = '<>';
                break;
            case 'in':
                outOperator = 'contains';
                break;
            case 'lt':
                outOperator = '<';
                break;
            case 'lte':
                outOperator = '<=';
                break;
            case 'gt':
                outOperator = '>';
                break;
            case 'gte':
                outOperator = '>=';
                break;
            case 'between':
                outOperator = null;
                break;
            case 'is':
                outOperator = 'contains';
                break;
            case 'isNot':
                outOperator = '<>';
                break;
            case 'before':
                outOperator = '<';
                break;
            case 'after':
                outOperator = '>';
                break;
            case 'dateIs':
                outOperator = '=';
                break;
            case 'dateIsNot':
                outOperator = '<>';
                break;
            case 'dateBefore':
                outOperator = '<';
                break;
            case 'dateAfter':
                outOperator = '>';
                break;

            default:
                break;
        }
        return outOperator;
    }
}
